import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useSelector } from 'react-redux'
import { FormikProvider } from 'formik'
import { Grid, Box, Button, Chip } from '@mui/material'
import { FilterAlt, Delete } from '@mui/icons-material'

import useReportsFilters from '@/views/reports/hooks/use-reports-filters.hooks'
import { ApplicationState } from '@/state/root.models'
import { ADMIN_VIEW } from '@/constants/brand'

import { wageringSelector } from '@/state/wagering-contribution/slices/index.slices'
import { CategoriesList } from '@/state/wagering-contribution/models'
import { TransactionTypePayment } from '@/enums/transaction-type-payment.enum'

import ReportTypeFilter from './report-type.component'
import DateRangeFilterType from './date-range.component'
import { ReportsFiltersDrawer } from './reports-filters-drawer.component'

const ReportsFilters = () => {
  const { t } = useTranslation()

  const location = useLocation()

  const {
    formikReports,
    handleChangeSearch,
    handleBulkChangeSearch,
    submitSearchFilter,
  } = useReportsFilters()

  const { brand } = useSelector(({ auth }: ApplicationState) => auth)
  const { categoriesList } = useSelector(wageringSelector)

  const isPlayerCard = location.pathname.includes('/player-card')
  const [openDrawer, setOpenDrawer] = useState(false)

  const filtersValues = formikReports.values

  const activeChips: React.ReactNode[] = []
  let activeFiltersCount = 0

  const addChip = (key: string, label: string, displayValue: string) => {
    activeChips.push(
      <Chip
        key={key}
        label={`${label}: ${displayValue}`}
        onDelete={() => {
          const newFilters = handleChangeSearch(
            key,
            Array.isArray(filtersValues[key]) ? [] : '',
          )
          submitSearchFilter(newFilters)
        }}
        sx={{ margin: 0.5 }}
      />,
    )
    activeFiltersCount += 1
  }

  if (filtersValues.game && filtersValues.game.length > 0) {
    addChip('game', t('labels.game'), filtersValues.game.join(', '))
  }

  if (filtersValues.gameCategory && filtersValues.gameCategory.length > 0) {
    const categoryNames = filtersValues.gameCategory.map((id: string) => {
      const cat = categoriesList?.find(
        (c: CategoriesList) => c.id.toString() === id.toString(),
      )
      return cat ? cat.name : id
    })
    addChip('gameCategory', t('labels.gameCategory'), categoryNames.join(', '))
  }

  if (filtersValues.gameProvider && filtersValues.gameProvider.length > 0) {
    const providerNames = filtersValues.gameProvider.map((p: any) => p.name)
    addChip('gameProvider', t('labels.gameProvider'), providerNames.join(', '))
  }

  if (
    filtersValues.transactionTypePayment &&
    filtersValues.transactionTypePayment !== 'all'
  ) {
    const enumKey = Object.keys(TransactionTypePayment).find(
      (k) =>
        TransactionTypePayment[k as keyof typeof TransactionTypePayment] ===
        Number(filtersValues.transactionTypePayment),
    )
    if (enumKey) {
      addChip(
        'transactionTypePayment',
        t('labels.transactionTypePayment'),
        t(`reports.transactionTypePayment.${enumKey}`),
      )
    }
  }

  if (filtersValues.playerId) {
    addChip('playerId', t('labels.playerId'), filtersValues.playerId)
  }

  if (filtersValues.brandId) {
    addChip('brandId', t('labels.brand'), filtersValues.brandId)
  }

  const handleClearChips = () => {
    const clearValues: any = {
      game: [],
      gameCategory: [],
      gameProvider: [],
      transactionTypePayment: '',
    }
    if (!isPlayerCard) clearValues.playerId = ''
    if (brand.siteId === ADMIN_VIEW) clearValues.brandId = ''

    const newFilters = handleBulkChangeSearch(clearValues)
    submitSearchFilter(newFilters)
  }

  const handleApplyDrawer = (newFilters: any) => {
    const updatedFilters = handleBulkChangeSearch(newFilters)
    submitSearchFilter(updatedFilters)
  }

  const handleClearDrawer = () => {
    const clearValues: any = {
      game: [],
      gameCategory: [],
      gameProvider: [],
      transactionTypePayment: '',
    }
    if (!isPlayerCard) clearValues.playerId = ''
    if (brand.siteId === ADMIN_VIEW) clearValues.brandId = ''

    const newFilters = handleBulkChangeSearch(clearValues)
    submitSearchFilter(newFilters)
  }

  const handleMainFilterChange = (field: string, value: any) => {
    const newFilters = handleChangeSearch(field, value)
    submitSearchFilter(newFilters)
  }

  return (
    <Box mb={3}>
      <FormikProvider value={formikReports}>
        <Box borderRadius={10} px={1.5} p={1}>
          <Box mb={2}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={3} lg={2}>
                <ReportTypeFilter handleChangeSearch={handleMainFilterChange} />
              </Grid>
              <DateRangeFilterType
                handleChangeSearch={handleMainFilterChange}
              />

              <Grid
                item
                xs={12}
                md={4}
                lg={2}
                display="flex"
                justifyContent="flex-end"
                sx={{ ml: 'auto' }}
              >
                <Button
                  onClick={() => setOpenDrawer(true)}
                  variant="contained"
                  color="inherit"
                  sx={{
                    height: '40px',
                    backgroundColor: '#fff',
                    color: '#000',
                    '&:hover': { backgroundColor: '#f0f0f0' },
                    mt: 1,
                  }}
                  startIcon={<FilterAlt />}
                >
                  {t('transaction.filters')}
                  {activeFiltersCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        backgroundColor: '#323a46',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                      }}
                    >
                      {activeFiltersCount}
                    </Box>
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Grid item xs={12}>
            <Box display="flex" flexWrap="wrap" mt={1}>
              {activeChips}
              {activeChips.length > 0 && (
                <Button
                  color="error"
                  onClick={handleClearChips}
                  sx={{
                    textTransform: 'none',
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: 'error.lighter',
                    },
                  }}
                  startIcon={<Delete />}
                >
                  {t('labels.clear')}
                </Button>
              )}
            </Box>
          </Grid>
        </Box>

        <ReportsFiltersDrawer
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          currentFilters={filtersValues}
          onApply={handleApplyDrawer}
          onClear={handleClearDrawer}
        />
      </FormikProvider>
    </Box>
  )
}
export default ReportsFilters
